import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { allComics } from "@/mocks/comics";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { BreadcrumbItem } from "@/components/feature/Breadcrumb";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(
    () => allComics.find((c) => c.id === Number(id)),
    [id],
  );

  if (!comic) {
    return (
      <main className="pt-20 pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6 text-center py-20">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-book-2-line text-3xl"></i>
          </div>
          <h1 className="mt-6 text-2xl font-heading font-bold text-foreground-900">
            漫画不存在
          </h1>
          <p className="mt-2 text-foreground-500">
            您查找的漫画可能已被移除或链接错误
          </p>
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", path: "/" },
    { label: "漫画分类", path: "/category/all" },
    { label: comic.title },
  ];

  const comicSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: comic.title,
    author: { "@type": "Person", name: comic.author },
    description: comic.description,
    image: comic.cover,
    genre: comic.categories,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: comic.rating,
      bestRating: "10",
      ratingCount: comic.views,
    },
    inLanguage: "zh-CN",
  };

  const relatedComics = allComics
    .filter(
      (c) =>
        c.id !== comic.id &&
        c.categories.some((cat) => comic.categories.includes(cat)),
    )
    .slice(0, 5);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(comicSchema)}
      </script>
      <main className="pt-20 pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-72">
              <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                <img
                  src={comic.coverLarge}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫在线免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {comic.status === "连载中" ? (
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-500/15 text-primary-500 text-xs font-semibold">
                    连载中
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-500/15 text-accent-500 text-xs font-semibold">
                    已完结
                  </span>
                )}
                {comic.categories.map((cat) => (
                  <span
                    key={cat}
                    className="px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-xs"
                  >
                    {cat}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {comic.title}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                作者：{comic.author}
              </p>

              <div className="flex items-center gap-5 mt-4 text-sm">
                <span className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-star-fill text-primary-500 text-sm"></i>
                  <span className="font-semibold text-foreground-800">
                    {comic.rating}
                  </span>
                </span>
                <span className="text-foreground-500">
                  {(comic.views / 10000).toFixed(1)} 万次阅读
                </span>
                <span className="text-foreground-500">
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                {comic.description}
              </p>

              <div className="flex items-center gap-2 flex-wrap mt-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line text-base"></i>
                  开始阅读
                </button>
                <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-all duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line text-base"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
              章节列表
            </h2>
            <div className="bg-background-100 rounded-xl overflow-hidden">
              <div className="divide-y divide-background-200/60">
                {[...comic.chapters].reverse().map((chapter, idx) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-background-200/40 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xs font-medium">
                        {chapter.number}
                      </span>
                      <span className="text-sm font-medium text-foreground-800">
                        {chapter.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span>{chapter.updatedAt}</span>
                      <span>{(chapter.views / 10000).toFixed(1)}万</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/comic/${c.id}`}
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    className="group block cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={c.cover}
                        alt={c.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                      {c.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}